import React, { useState, useEffect } from 'react'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
} from '@dnd-kit/core'
import { restrictToWindowEdges, snapCenterToCursor } from '@dnd-kit/modifiers'
import { useAuth } from '../../context/AuthContext'
import { useAppData } from '../../context/AppContext'
import { taskService, Task, TaskLoggedHoursBreakdown, minutesToHours, hoursToMinutes, formatHours, toMinutesNumber } from '../../services/taskService'
import { Modal } from '../ui/Modal'
import { Badge } from '../ui/Badge'
import { Plus, EditIcon, Trash2, MessageSquare, UserCheck, GripVertical, ChevronDown, ChevronUp } from '../icons/Icons'
import { BulkUploadModal } from '../shared/BulkUploadModal'
import { toDateOnly } from '../../utils/validators'
import s from './shared.module.css'

type ViewMode = 'list' | 'kanban' | 'tree'

const STATUSES: Task['status'][] = ['todo', 'in_progress', 'done', 'blocked']
const PRIORITIES: Task['priority'][] = ['low', 'medium', 'high', 'critical']

const STATUS_LABELS = {
  todo: 'To Do',
  in_progress: 'In Progress',
  done: 'Completed',
  blocked: 'Blocked'
}

const PRIORITY_COLORS = {
  low: '#6c757d',
  medium: '#0d6efd',
  high: '#fd7e14',
  critical: '#dc3545'
}

const COLUMN_COLORS: Record<string, string> = {
  todo: '#6c757d',
  in_progress: '#0d6efd',
  done: '#198754',
  blocked: '#dc3545',
}

// ── Module-level components so React never remounts them during a drag ──────

interface KanbanCardProps {
  task: Task
  isChild?: boolean
  subtasks: Task[]
  assignments: any[]
  collapsedParents: Set<string>
  allTaskAssignments: { [taskId: string]: any[] }
  onEdit: (task: Task) => void
  onToggleCollapse: (taskId: string) => void
}

function KanbanCard({
  task,
  isChild = false,
  subtasks,
  assignments,
  collapsedParents,
  allTaskAssignments,
  onEdit,
  onToggleCollapse,
}: KanbanCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: task.id })
  const isCollapsed = collapsedParents.has(task.id)
  const hasSubtasks = subtasks.length > 0
  const assignedNames = assignments.map((a: any) => a.resource_title || 'Unknown').join(', ')

  return (
    <div ref={setNodeRef} style={{ opacity: isDragging ? 0.4 : 1 }}>
      <div
        style={{
          backgroundColor: 'white',
          border: '1px solid #dee2e6',
          borderLeft: `3px solid ${PRIORITY_COLORS[task.priority]}`,
          borderRadius: 6,
          padding: isChild ? '8px 10px' : 12,
          marginBottom: isChild ? 6 : 0,
          boxShadow: isChild ? 'none' : '0 1px 3px rgba(0,0,0,0.06)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          {/* Drag handle — listeners/attributes here so only handle activates drag */}
          <div
            {...listeners}
            {...attributes}
            style={{
              cursor: isDragging ? 'grabbing' : 'grab',
              color: '#adb5bd',
              flexShrink: 0,
              marginTop: 2,
              touchAction: 'none',
            }}
            title="Drag to move"
          >
            <GripVertical size={14} />
          </div>

          {/* Card content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 4 }}>
              <div
                style={{ fontWeight: isChild ? 500 : 600, fontSize: isChild ? 12 : 13, cursor: 'pointer', lineHeight: 1.3 }}
                onClick={() => onEdit(task)}
              >
                {task.title}
              </div>
              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                {hasSubtasks && (
                  <button
                    onClick={() => onToggleCollapse(task.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6c757d', padding: 0, display: 'flex', alignItems: 'center' }}
                    title={isCollapsed ? 'Show subtasks' : 'Hide subtasks'}
                  >
                    {isCollapsed ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
                  </button>
                )}
                <button
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6c757d', padding: '2px 4px', display: 'flex', alignItems: 'center' }}
                  onClick={() => onEdit(task)}
                  title="Edit"
                >
                  <EditIcon size={12} />
                </button>
              </div>
            </div>

            {task.description && !isChild && (
              <div style={{ fontSize: 11, color: '#6c757d', marginTop: 4, lineHeight: 1.4 }}>
                {task.description.length > 80 ? task.description.substring(0, 80) + '...' : task.description}
              </div>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6, alignItems: 'center' }}>
              <Badge style={{ backgroundColor: PRIORITY_COLORS[task.priority], color: 'white', fontSize: 10 }}>
                {task.priority}
              </Badge>
              {task.due_date && (
                <span style={{ fontSize: 10, color: '#6c757d' }}>{task.due_date}</span>
              )}
              {assignedNames && (
                <span style={{ fontSize: 10, color: '#495057', backgroundColor: '#e9ecef', borderRadius: 3, padding: '1px 5px' }}>
                  {assignedNames}
                </span>
              )}
              {hasSubtasks && (
                <span style={{ fontSize: 10, color: '#6c757d' }}>
                  {subtasks.length} subtask{subtasks.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Subtasks */}
      {hasSubtasks && !isCollapsed && (
        <div style={{ paddingLeft: 14, marginTop: 4 }}>
          {subtasks.map(sub => (
            <KanbanCard
              key={sub.id}
              task={sub}
              isChild
              subtasks={[]}
              assignments={allTaskAssignments[sub.id] || []}
              collapsedParents={collapsedParents}
              allTaskAssignments={allTaskAssignments}
              onEdit={onEdit}
              onToggleCollapse={onToggleCollapse}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface KanbanColumnProps {
  status: Task['status']
  tasks: Task[]
  allTaskAssignments: { [taskId: string]: any[] }
  collapsedParents: Set<string>
  onEdit: (task: Task) => void
  onToggleCollapse: (taskId: string) => void
}

function KanbanColumn({ status, tasks, allTaskAssignments, collapsedParents, onEdit, onToggleCollapse }: KanbanColumnProps) {
  const rootTasksInColumn = tasks.filter(t => t.status === status && !t.parent_task_id)
  const totalCount = tasks.filter(t => t.status === status).length
  const { setNodeRef, isOver } = useDroppable({ id: status })

  return (
    <div
      style={{
        flex: '0 0 300px',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 260px)',
      }}
    >
      {/* Column header */}
      <div
        style={{
          padding: '10px 14px',
          borderRadius: '8px 8px 0 0',
          backgroundColor: COLUMN_COLORS[status],
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {STATUS_LABELS[status]}
        </span>
        <span style={{ fontSize: 12, backgroundColor: 'rgba(255,255,255,0.25)', borderRadius: 10, padding: '1px 8px', fontWeight: 600 }}>
          {totalCount}
        </span>
      </div>

      {/* Drop zone */}
      <div
        ref={setNodeRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 10,
          backgroundColor: isOver ? '#e8f0fe' : '#f8f9fa',
          borderRadius: '0 0 8px 8px',
          border: isOver ? '2px dashed #0d6efd' : '2px solid transparent',
          transition: 'background-color 0.15s, border-color 0.15s',
          minHeight: 120,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {rootTasksInColumn.length === 0 && (
          <div style={{ textAlign: 'center', color: '#adb5bd', fontSize: 12, padding: '20px 0', userSelect: 'none' }}>
            Drop tasks here
          </div>
        )}
        {rootTasksInColumn.map(task => (
          <KanbanCard
            key={task.id}
            task={task}
            subtasks={tasks.filter(t => t.parent_task_id === task.id)}
            assignments={allTaskAssignments[task.id] || []}
            collapsedParents={collapsedParents}
            allTaskAssignments={allTaskAssignments}
            onEdit={onEdit}
            onToggleCollapse={onToggleCollapse}
          />
        ))}
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────

export default function TaskManagement() {
  const { organization, user, orgMember } = useAuth()
  const { projects, refreshData } = useAppData()
  const orgId = organization!.id

  const canBulkUpload = orgMember?.user_type === 'owner' || orgMember?.user_type === 'admin' || orgMember?.user_type === 'manager'

  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [selectedProjectId, setSelectedProjectId] = useState<string>('')
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [showBulkUpload, setShowBulkUpload] = useState(false)
  const [showCommentModal, setShowCommentModal] = useState(false)
  const [showAssignmentModal, setShowAssignmentModal] = useState(false)
  const [editing, setEditing] = useState<Task | null>(null)
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [comments, setComments] = useState<any[]>([])
  const [newComment, setNewComment] = useState('')
  const [allocatedResources, setAllocatedResources] = useState<any[]>([])
  const [taskAssignments, setTaskAssignments] = useState<any[]>([])
  const [assignmentError, setAssignmentError] = useState('')
  const [allTaskAssignments, setAllTaskAssignments] = useState<{ [taskId: string]: any[] }>({})
  const [collapsedParents, setCollapsedParents] = useState<Set<string>>(new Set())
  const [activeTask, setActiveTask] = useState<Task | null>(null)
  const [overColumnId, setOverColumnId] = useState<Task['status'] | null>(null)
  const [showLoggedHoursModal, setShowLoggedHoursModal] = useState(false)
  const [loggedHoursBreakdown, setLoggedHoursBreakdown] = useState<TaskLoggedHoursBreakdown[]>([])
  const [loggedHoursLoading, setLoggedHoursLoading] = useState(false)
  const [loggedHoursTask, setLoggedHoursTask] = useState<Task | null>(null)

  const [form, setForm] = useState({
    title: '',
    description: '',
    status: 'todo' as Task['status'],
    priority: 'medium' as Task['priority'],
    start_date: '',
    due_date: '',
    estimated_hours: '',
    parent_task_id: ''
  })

  useEffect(() => {
    if (selectedProjectId) {
      loadTasks()
    }
  }, [selectedProjectId])

  const loadTasks = async () => {
    if (!selectedProjectId) return
    setLoading(true)
    try {
      const data = await taskService.getProjectTasks(selectedProjectId)
      setTasks(data)

      // Load assignments for all tasks
      const assignmentsMap: { [taskId: string]: any[] } = {}
      await Promise.all(
        data.map(async (task) => {
          try {
            const assignments = await taskService.getTaskAssignments(task.id)
            assignmentsMap[task.id] = assignments
          } catch (err) {
            assignmentsMap[task.id] = []
          }
        })
      )
      setAllTaskAssignments(assignmentsMap)
    } catch (error) {
      console.error('Failed to load tasks:', error)
    } finally {
      setLoading(false)
    }
  }

  const openAdd = () => {
    setEditing(null)
    setForm({
      title: '',
      description: '',
      status: 'todo',
      priority: 'medium',
      start_date: '',
      due_date: '',
      estimated_hours: '',
      parent_task_id: ''
    })
    setSaveError('')
    setShowModal(true)
  }

  const openEdit = (task: Task) => {
    setEditing(task)
    setForm({
      title: task.title,
      description: task.description || '',
      status: task.status,
      priority: task.priority,
      start_date: toDateOnly(task.start_date),
      due_date: toDateOnly(task.due_date),
      estimated_hours: task.estimated_minutes ? minutesToHours(task.estimated_minutes).toString() : '',
      parent_task_id: task.parent_task_id || ''
    })
    setSaveError('')
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (saving || !selectedProjectId) return
    setSaving(true)
    setSaveError('')

    try {
      // Validate against project dates
      const validation = await taskService.validateTaskDates(
        selectedProjectId,
        form.start_date || undefined,
        form.due_date || undefined
      )

      if (!validation.valid) {
        setSaveError(validation.error || 'Invalid task dates')
        setSaving(false)
        return
      }

      // Validate against parent task dates (if parent exists)
      if (form.parent_task_id) {
        const parentTask = getParentTask(form.parent_task_id)
        if (parentTask) {
          if (form.start_date && parentTask.start_date) {
            if (form.start_date < parentTask.start_date) {
              setSaveError(`Start date cannot be before parent task start date (${parentTask.start_date})`)
              setSaving(false)
              return
            }
          }
          if (form.due_date && parentTask.due_date) {
            if (form.due_date > parentTask.due_date) {
              setSaveError(`Due date cannot be after parent task due date (${parentTask.due_date})`)
              setSaving(false)
              return
            }
          }
          if (form.start_date && parentTask.due_date) {
            if (form.start_date > parentTask.due_date) {
              setSaveError(`Start date cannot be after parent task due date (${parentTask.due_date})`)
              setSaving(false)
              return
            }
          }
          if (form.due_date && parentTask.start_date) {
            if (form.due_date < parentTask.start_date) {
              setSaveError(`Due date cannot be before parent task start date (${parentTask.start_date})`)
              setSaving(false)
              return
            }
          }
        }
      }

      const basePayload = {
        org_id: orgId,
        project_id: selectedProjectId,
        title: form.title,
        description: form.description || undefined,
        status: form.status,
        priority: form.priority,
        start_date: form.start_date || undefined,
        due_date: form.due_date || undefined,
        estimated_minutes: form.estimated_hours ? hoursToMinutes(parseFloat(form.estimated_hours)) : undefined,
        parent_task_id: form.parent_task_id || undefined,
        created_by: user!.id
      }

      if (editing) {
        await taskService.updateTask(editing.id, basePayload)
      } else {
        await taskService.createTask({ ...basePayload, order_index: tasks.length })
      }

      setShowModal(false)
      await loadTasks()
    } catch (err: any) {
      setSaveError(err?.message || 'Failed to save task')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this task?')) return
    try {
      await taskService.deleteTask(id)
      await loadTasks()
    } catch (error) {
      console.error('Failed to delete task:', error)
    }
  }

  const handleStatusChange = async (taskId: string, newStatus: Task['status']) => {
    try {
      await taskService.updateTask(taskId, { status: newStatus })
      await loadTasks()
    } catch (error) {
      console.error('Failed to update task status:', error)
    }
  }

  const openAssignmentModal = async (task: Task) => {
    setSelectedTask(task)
    setAssignmentError('')
    setShowAssignmentModal(true)

    try {
      // Get task from tasks array to access project_id
      const taskData = tasks.find(t => t.id === task.id)
      if (!taskData) return

      // Load allocated resources for this project
      const resources = await taskService.getAllocatedResources(taskData.project_id, orgId)
      setAllocatedResources(resources)

      // Load current assignments
      const assignments = await taskService.getTaskAssignments(task.id)
      setTaskAssignments(assignments)
    } catch (error: any) {
      setAssignmentError(error?.message || 'Failed to load resource data')
    }
  }

  const handleAssignResource = async (resourceId: string) => {
    if (!selectedTask) return
    setAssignmentError('')

    try {
      await taskService.assignResourceToTask(selectedTask.id, resourceId, orgId, user!.id)

      // Reload assignments
      const assignments = await taskService.getTaskAssignments(selectedTask.id)
      setTaskAssignments(assignments)
      setAllTaskAssignments(prev => ({ ...prev, [selectedTask.id]: assignments }))
    } catch (error: any) {
      setAssignmentError(error?.message || 'Failed to assign resource')
    }
  }

  const handleRemoveAssignment = async (assignmentId: string) => {
    if (!confirm('Remove this resource assignment?')) return
    setAssignmentError('')

    try {
      await taskService.removeTaskAssignment(assignmentId)

      // Reload assignments
      if (selectedTask) {
        const assignments = await taskService.getTaskAssignments(selectedTask.id)
        setTaskAssignments(assignments)
        setAllTaskAssignments(prev => ({ ...prev, [selectedTask.id]: assignments }))
      }
    } catch (error: any) {
      setAssignmentError(error?.message || 'Failed to remove assignment')
    }
  }

  const openComments = async (task: Task) => {
    setSelectedTask(task)
    try {
      const data = await taskService.getTaskComments(task.id)
      setComments(data)
      setShowCommentModal(true)
    } catch (error) {
      console.error('Failed to load comments:', error)
    }
  }

  const handleAddComment = async () => {
    if (!selectedTask || !newComment.trim()) return
    try {
      await taskService.addComment(selectedTask.id, user!.id, orgId, newComment)
      const data = await taskService.getTaskComments(selectedTask.id)
      setComments(data)
      setNewComment('')
    } catch (error) {
      console.error('Failed to add comment:', error)
    }
  }

  const openLoggedHoursBreakdown = async (task: Task) => {
    setLoggedHoursTask(task)
    setShowLoggedHoursModal(true)
    setLoggedHoursLoading(true)
    setLoggedHoursBreakdown([])
    try {
      const data = await taskService.getTaskLoggedHoursBreakdown(task.id)
      setLoggedHoursBreakdown(data)
    } catch (error) {
      console.error('Failed to load logged hours breakdown:', error)
    } finally {
      setLoggedHoursLoading(false)
    }
  }

  const getSubtasks = (parentId: string) => {
    return tasks.filter(t => t.parent_task_id === parentId)
  }

  const getTaskDepth = (taskId: string): number => {
    const task = tasks.find(t => t.id === taskId)
    if (!task || !task.parent_task_id) return 0
    return 1 + getTaskDepth(task.parent_task_id)
  }

  const getParentTask = (parentId: string | undefined): Task | null => {
    if (!parentId) return null
    return tasks.find(t => t.id === parentId) || null
  }

  const getDateConstraints = () => {
    const parentTask = getParentTask(form.parent_task_id)
    if (parentTask) {
      return {
        minDate: parentTask.start_date || undefined,
        maxDate: parentTask.due_date || undefined,
        hasConstraints: true
      }
    }
    return {
      minDate: undefined,
      maxDate: undefined,
      hasConstraints: false
    }
  }

  const renderTaskRow = (task: Task, depth: number = 0): React.ReactNode => {
    const indent = depth * 20
    const subtasks = getSubtasks(task.id)
    const assignments = allTaskAssignments[task.id] || []
    const assignedResourceNames = assignments.map(a => a.resource_title || 'Unknown').join(', ')

    return (
      <React.Fragment key={task.id}>
        <tr style={{ backgroundColor: depth > 0 ? '#f8f9fa' : 'white' }}>
          <td>
            <div style={{ marginLeft: indent }}>
              {depth > 0 && <span style={{ marginRight: 8 }}>└─</span>}
              <strong>{task.title}</strong>
              {task.description && (
                <div style={{ fontSize: 12, color: '#6c757d', marginTop: 4 }}>
                  {task.description}
                </div>
              )}
            </div>
          </td>
          <td>
            <select
              value={task.status}
              onChange={(e) => handleStatusChange(task.id, e.target.value as Task['status'])}
              style={{ padding: '4px 8px', border: '1px solid #dee2e6', borderRadius: 4 }}
            >
              {STATUSES.map(status => (
                <option key={status} value={status}>
                  {STATUS_LABELS[status]}
                </option>
              ))}
            </select>
          </td>
          <td>
            <Badge
              style={{
                backgroundColor: PRIORITY_COLORS[task.priority],
                color: 'white'
              }}
            >
              {task.priority}
            </Badge>
          </td>
          <td>
            <div style={{ fontSize: 13 }}>
              {assignedResourceNames || '—'}
            </div>
          </td>
          <td>{task.due_date || '—'}</td>
          <td>{task.estimated_minutes ? minutesToHours(task.estimated_minutes) : '—'}</td>
          <td>
            {(() => {
              const logged = toMinutesNumber((task as any).logged_minutes)
              const draft = toMinutesNumber((task as any).draft_minutes)
              const budget = toMinutesNumber(task.estimated_minutes)
              const hasLogged = logged > 0 || draft > 0
              if (!hasLogged) return <span style={{ color: '#adb5bd' }}>—</span>
              const isOver = budget > 0 && logged > budget
              return (
                <button
                  onClick={() => openLoggedHoursBreakdown(task)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    textAlign: 'left',
                    font: 'inherit',
                    color: isOver ? '#dc3545' : '#0d6efd',
                    fontWeight: isOver ? 600 : 500,
                  }}
                  title="Click to see who logged hours"
                >
                  {formatHours(logged)}
                  {budget > 0 ? ` / ${minutesToHours(budget)}h` : ''}
                  {draft > 0 && (
                    <span style={{ fontSize: 11, color: '#6c757d', display: 'block', fontWeight: 400 }}>
                      +{formatHours(draft)} draft
                    </span>
                  )}
                </button>
              )
            })()}
          </td>
          <td>
            <div className={s.actions}>
              <button
                className={`${s.actionBtn} ${s.edit}`}
                onClick={() => openAssignmentModal(task)}
                title="Assign Resources"
              >
                <UserCheck size={14} />
              </button>
              <button
                className={`${s.actionBtn} ${s.edit}`}
                onClick={() => openComments(task)}
                title="Comments"
              >
                <MessageSquare size={14} />
              </button>
              <button
                className={`${s.actionBtn} ${s.edit}`}
                onClick={() => openEdit(task)}
              >
                <EditIcon size={14} />
              </button>
              <button
                className={`${s.actionBtn} ${s.del}`}
                onClick={() => handleDelete(task.id)}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </td>
        </tr>
        {subtasks.map(subtask => renderTaskRow(subtask, depth + 1))}
      </React.Fragment>
    )
  }

  const renderListView = () => (
    <table className={s.table}>
      <thead>
        <tr>
          <th>Task</th>
          <th>Status</th>
          <th>Priority</th>
          <th>Assigned To</th>
          <th>Due Date</th>
          <th>Est. Hours</th>
          <th>Logged Hours</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {tasks.length === 0 && (
          <tr>
            <td colSpan={8} className={s.emptyState}>
              No tasks found
            </td>
          </tr>
        )}
        {tasks.filter(t => !t.parent_task_id).map(task => renderTaskRow(task))}
      </tbody>
    </table>
  )

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  )

  const toggleCollapsed = (taskId: string) => {
    setCollapsedParents(prev => {
      const next = new Set(prev)
      if (next.has(taskId)) next.delete(taskId)
      else next.add(taskId)
      return next
    })
  }

  const handleDragStart = (event: DragStartEvent) => {
    const task = tasks.find(t => t.id === event.active.id)
    setActiveTask(task || null)
  }

  const handleDragOver = (event: DragOverEvent) => {
    const overId = event.over?.id as string | undefined
    if (overId && STATUSES.includes(overId as Task['status'])) {
      setOverColumnId(overId as Task['status'])
    } else {
      setOverColumnId(null)
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveTask(null)
    setOverColumnId(null)
    const { active, over } = event
    if (!over) return

    const overId = over.id as string
    if (!STATUSES.includes(overId as Task['status'])) return

    const newStatus = overId as Task['status']
    const draggedTask = tasks.find(t => t.id === active.id)
    if (!draggedTask || draggedTask.status === newStatus) return

    setTasks(prev => prev.map(t => t.id === draggedTask.id ? { ...t, status: newStatus } : t))

    try {
      await taskService.updateTask(draggedTask.id, { status: newStatus })
    } catch (error) {
      console.error('Failed to update task status:', error)
      await loadTasks()
    }
  }

  const renderKanbanView = () => (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      modifiers={[restrictToWindowEdges]}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 8 }}>
        {STATUSES.map(status => (
          <KanbanColumn
            key={status}
            status={status}
            tasks={tasks}
            allTaskAssignments={allTaskAssignments}
            collapsedParents={collapsedParents}
            onEdit={openEdit}
            onToggleCollapse={toggleCollapsed}
          />
        ))}
      </div>

      <DragOverlay dropAnimation={{ duration: 150, easing: 'ease' }} modifiers={[snapCenterToCursor]}>
        {activeTask && (
          <div
            style={{
              backgroundColor: 'white',
              border: `3px solid ${PRIORITY_COLORS[activeTask.priority]}`,
              borderRadius: 6,
              padding: 12,
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              width: 276,
              opacity: 0.95,
              cursor: 'grabbing',
              pointerEvents: 'none',
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{activeTask.title}</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <Badge style={{ backgroundColor: PRIORITY_COLORS[activeTask.priority], color: 'white', fontSize: 10 }}>
                {activeTask.priority}
              </Badge>
              {activeTask.due_date && (
                <span style={{ fontSize: 10, color: '#6c757d' }}>{activeTask.due_date}</span>
              )}
            </div>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )

  const renderTreeNode = (task: Task, depth: number = 0): React.ReactNode => {
    const subtasks = getSubtasks(task.id)
    const marginLeft = depth * 32

    return (
      <div key={task.id} style={{ marginBottom: depth === 0 ? 24 : 8, marginLeft }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: depth === 0 ? 12 : 10,
            backgroundColor: depth === 0 ? '#f8f9fa' : 'white',
            border: depth > 0 ? '1px solid #dee2e6' : 'none',
            borderRadius: 4,
            marginBottom: subtasks.length > 0 ? 8 : 0
          }}
        >
          <div style={{ flex: 1 }}>
            {depth > 0 && <span style={{ marginRight: 8 }}>└─</span>}
            <strong>{task.title}</strong>
            {task.description && (
              <div style={{ fontSize: 12, color: '#6c757d', marginTop: 4 }}>
                {task.description}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Badge
              style={{
                backgroundColor: PRIORITY_COLORS[task.priority],
                color: 'white',
                fontSize: depth === 0 ? 12 : 11
              }}
            >
              {task.priority}
            </Badge>
            <span style={{ fontSize: depth === 0 ? 12 : 11, color: '#6c757d' }}>
              {STATUS_LABELS[task.status]}
            </span>
            <button className={`${s.actionBtn} ${s.edit}`} onClick={() => openEdit(task)}>
              <EditIcon size={14} />
            </button>
          </div>
        </div>
        {subtasks.length > 0 && (
          <div style={{ marginLeft: 32 }}>
            {subtasks.map(subtask => renderTreeNode(subtask, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  const renderTreeView = () => {
    const rootTasks = tasks.filter(t => !t.parent_task_id)

    return (
      <div style={{ padding: 16 }}>
        {rootTasks.map(task => renderTreeNode(task))}
      </div>
    )
  }

  // Allow only tasks at depth 0 or 1 to be parents (max 3 levels total)
  const availableParentTasks = tasks.filter(t => {
    if (t.id === editing?.id) return false
    const depth = getTaskDepth(t.id)
    return depth < 2 // Allow depth 0 (root) and depth 1 (child) to be parents
  })

  return (
    <div>
      <div className={s.pageHeader}>
        <div>
          <h2 className={s.pageTitle}>Task Management</h2>
          <p className={s.pageSubtitle}>
            {selectedProjectId ? `${tasks.length} task${tasks.length !== 1 ? 's' : ''}` : 'Select a project'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <select
            className={s.filterSelect}
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            style={{ minWidth: 200 }}
          >
            <option value="">Select Project...</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
          {canBulkUpload && (
            <button
              className={s.secondaryBtn}
              onClick={() => setShowBulkUpload(true)}
            >
              Bulk Upload
            </button>
          )}
          <button
            className={s.primaryBtn}
            onClick={openAdd}
            disabled={!selectedProjectId}
          >
            <Plus size={14} /> Add Task
          </button>
        </div>
      </div>

      {selectedProjectId && (
        <>
          <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
            <button
              className={viewMode === 'list' ? s.primaryBtn : s.secondaryBtn}
              onClick={() => setViewMode('list')}
            >
              List View
            </button>
            <button
              className={viewMode === 'kanban' ? s.primaryBtn : s.secondaryBtn}
              onClick={() => setViewMode('kanban')}
            >
              Kanban View
            </button>
            <button
              className={viewMode === 'tree' ? s.primaryBtn : s.secondaryBtn}
              onClick={() => setViewMode('tree')}
            >
              Tree View
            </button>
          </div>

          <div className={s.card}>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center' }}>Loading tasks...</div>
            ) : (
              <>
                {viewMode === 'list' && renderListView()}
                {viewMode === 'kanban' && renderKanbanView()}
                {viewMode === 'tree' && renderTreeView()}
              </>
            )}
          </div>
        </>
      )}

      {showBulkUpload && (
        <BulkUploadModal
          entityType="tasks"
          orgId={orgId}
          userId={user!.id}
          projects={projects}
          onClose={() => setShowBulkUpload(false)}
          onSuccess={async () => {
            setShowBulkUpload(false)
            if (selectedProjectId) {
              const updated = await taskService.getProjectTasks(selectedProjectId)
              setTasks(updated)
            }
          }}
        />
      )}

      <Modal
        open={showModal}
        title={editing ? 'Edit Task' : 'Add Task'}
        onClose={() => setShowModal(false)}
        maxWidth={600}
        footer={
          <>
            <button className={s.secondaryBtn} onClick={() => setShowModal(false)}>
              Cancel
            </button>
            <button
              className={s.primaryBtn}
              form="taskForm"
              type="submit"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </>
        }
      >
        <form id="taskForm" onSubmit={handleSubmit}>
          {saveError && <div className={s.formError}>{saveError}</div>}
          <div className={s.formGroup}>
            <label>Task Title *</label>
            <input
              value={form.title}
              onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              required
            />
          </div>
          <div className={s.formGroup}>
            <label>Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              rows={3}
            />
          </div>
          <div className={s.formRow}>
            <div className={s.formGroup}>
              <label>Status *</label>
              <select
                value={form.status}
                onChange={(e) => setForm(f => ({ ...f, status: e.target.value as Task['status'] }))}
                required
              >
                {STATUSES.map(status => (
                  <option key={status} value={status}>
                    {STATUS_LABELS[status]}
                  </option>
                ))}
              </select>
            </div>
            <div className={s.formGroup}>
              <label>Priority *</label>
              <select
                value={form.priority}
                onChange={(e) => setForm(f => ({ ...f, priority: e.target.value as Task['priority'] }))}
                required
              >
                {PRIORITIES.map(priority => (
                  <option key={priority} value={priority}>
                    {priority.charAt(0).toUpperCase() + priority.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className={s.formRow}>
            <div className={s.formGroup}>
              <label>Start Date</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))}
                min={getDateConstraints().minDate}
                max={getDateConstraints().maxDate}
              />
              {getDateConstraints().hasConstraints && getDateConstraints().minDate && (
                <small style={{ fontSize: 11, color: '#6c757d', marginTop: 4, display: 'block' }}>
                  Must be on or after {getDateConstraints().minDate}
                </small>
              )}
            </div>
            <div className={s.formGroup}>
              <label>Due Date</label>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm(f => ({ ...f, due_date: e.target.value }))}
                min={getDateConstraints().minDate}
                max={getDateConstraints().maxDate}
              />
              {getDateConstraints().hasConstraints && getDateConstraints().maxDate && (
                <small style={{ fontSize: 11, color: '#6c757d', marginTop: 4, display: 'block' }}>
                  Must be on or before {getDateConstraints().maxDate}
                </small>
              )}
            </div>
          </div>
          <div className={s.formRow}>
            <div className={s.formGroup}>
              <label>Estimated Hours</label>
              <input
                type="number"
                step="0.25"
                min="0"
                value={form.estimated_hours}
                onChange={(e) => setForm(f => ({ ...f, estimated_hours: e.target.value }))}
              />
            </div>
            <div className={s.formGroup}>
              <label>Parent Task (Max 3 levels)</label>
              <select
                value={form.parent_task_id}
                onChange={(e) => setForm(f => ({ ...f, parent_task_id: e.target.value }))}
              >
                <option value="">None (Root Level)</option>
                {availableParentTasks.map(task => {
                  const depth = getTaskDepth(task.id)
                  const prefix = depth > 0 ? '  '.repeat(depth) + '└─ ' : ''
                  return (
                    <option key={task.id} value={task.id}>
                      {prefix}{task.title}
                    </option>
                  )
                })}
              </select>
            </div>
          </div>
        </form>
      </Modal>

      <Modal
        open={showCommentModal}
        title={`Comments - ${selectedTask?.title}`}
        onClose={() => setShowCommentModal(false)}
        maxWidth={600}
      >
        <div style={{ marginBottom: 16 }}>
          {comments.length === 0 ? (
            <p style={{ color: '#6c757d', textAlign: 'center', padding: 20 }}>
              No comments yet
            </p>
          ) : (
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              {comments.map((comment) => (
                <div
                  key={comment.id}
                  style={{
                    padding: 12,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 4,
                    marginBottom: 8
                  }}
                >
                  <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 4 }}>
                    {new Date(comment.created_at).toLocaleString()}
                  </div>
                  <div>{comment.comment_text}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            rows={2}
            style={{ flex: 1, padding: 8, border: '1px solid #dee2e6', borderRadius: 4 }}
          />
          <button
            className={s.primaryBtn}
            onClick={handleAddComment}
            disabled={!newComment.trim()}
          >
            Add
          </button>
        </div>
      </Modal>

      <Modal
        open={showAssignmentModal}
        title={`Assign Resources - ${selectedTask?.title}`}
        onClose={() => setShowAssignmentModal(false)}
        maxWidth={600}
      >
        {assignmentError && (
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f8d7da', borderRadius: 4, color: '#721c24' }}>
            {assignmentError}
          </div>
        )}

        <div style={{ marginBottom: 24 }}>
          <h4 style={{ marginBottom: 12, fontSize: 14, fontWeight: 600 }}>Currently Assigned</h4>
          {taskAssignments.length === 0 ? (
            <p style={{ color: '#6c757d', fontSize: 13, fontStyle: 'italic' }}>
              No resources assigned yet
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {taskAssignments.map((assignment: any) => {
                const resource = allocatedResources.find(r => r.resource_id === assignment.resource_id)
                return (
                  <div
                    key={assignment.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: 8,
                      backgroundColor: '#f8f9fa',
                      borderRadius: 4
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 500 }}>{resource?.resource_title || 'Unknown'}</div>
                      <div style={{ fontSize: 12, color: '#6c757d' }}>{resource?.email}</div>
                    </div>
                    <button
                      className={`${s.actionBtn} ${s.del}`}
                      onClick={() => handleRemoveAssignment(assignment.id)}
                      title="Remove assignment"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div>
          <h4 style={{ marginBottom: 12, fontSize: 14, fontWeight: 600 }}>Allocated Resources</h4>
          {allocatedResources.length === 0 ? (
            <p style={{ color: '#6c757d', fontSize: 13, fontStyle: 'italic' }}>
              No resources allocated to this project. Please allocate resources first.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {allocatedResources
                .filter(resource => !taskAssignments.find(a => a.resource_id === resource.resource_id))
                .map((resource: any) => (
                  <div
                    key={resource.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: 8,
                      backgroundColor: '#f8f9fa',
                      borderRadius: 4
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 500 }}>{resource.resource_title}</div>
                      <div style={{ fontSize: 12, color: '#6c757d' }}>{resource.email}</div>
                    </div>
                    <button
                      className={s.secondaryBtn}
                      onClick={() => handleAssignResource(resource.resource_id)}
                      style={{ fontSize: 12, padding: '4px 12px' }}
                    >
                      Assign
                    </button>
                  </div>
                ))}
              {allocatedResources.filter(resource => !taskAssignments.find(a => a.resource_id === resource.resource_id)).length === 0 && (
                <p style={{ color: '#6c757d', fontSize: 13, fontStyle: 'italic' }}>
                  All allocated resources have been assigned
                </p>
              )}
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={showLoggedHoursModal}
        title={`Logged Hours - ${loggedHoursTask?.title}`}
        onClose={() => setShowLoggedHoursModal(false)}
        maxWidth={600}
      >
        {loggedHoursLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-neutral-600)' }}>
            Loading breakdown...
          </div>
        ) : loggedHoursBreakdown.length === 0 ? (
          <p style={{ color: '#6c757d', textAlign: 'center', padding: 20 }}>
            No resources assigned to this task yet.
          </p>
        ) : (
          <>
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              <table className={s.table} style={{ marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th>Resource</th>
                    <th>Logged</th>
                    <th>Draft</th>
                    <th>Last Entry</th>
                  </tr>
                </thead>
                <tbody>
                  {loggedHoursBreakdown.map((row) => {
                    const rowLogged = toMinutesNumber(row.logged_minutes)
                    const rowDraft = toMinutesNumber(row.draft_minutes)
                    const hasLogged = rowLogged > 0 || rowDraft > 0
                    return (
                      <tr
                        key={row.resource_id}
                        style={{ backgroundColor: hasLogged ? 'white' : '#f8f9fa' }}
                      >
                        <td>
                          <div style={{ fontWeight: 500 }}>{row.resource_name}</div>
                          <div style={{ fontSize: 11, color: '#6c757d' }}>{row.email}</div>
                        </td>
                        <td style={{ fontWeight: rowLogged > 0 ? 600 : 400 }}>
                          {rowLogged > 0 ? formatHours(rowLogged) : '—'}
                        </td>
                        <td style={{ color: '#6c757d' }}>
                          {rowDraft > 0 ? formatHours(rowDraft) : '—'}
                        </td>
                        <td style={{ fontSize: 12, color: '#6c757d' }}>
                          {row.last_entry_date || '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div
              style={{
                marginTop: 16,
                padding: '12px 16px',
                backgroundColor: '#f8f9fa',
                borderRadius: 6,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <span style={{ fontSize: 13, color: '#6c757d' }}>Budget: </span>
                <span style={{ fontWeight: 600, fontSize: 13 }}>
                  {(() => {
                    const budget = toMinutesNumber(loggedHoursTask?.estimated_minutes)
                    return budget > 0 ? `${minutesToHours(budget)}h` : '—'
                  })()}
                </span>
              </div>
              <div>
                <span style={{ fontSize: 13, color: '#6c757d' }}>Total Logged: </span>
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: 13,
                    color:
                      toMinutesNumber(loggedHoursTask?.estimated_minutes) > 0 &&
                      loggedHoursBreakdown.reduce((sum, r) => sum + toMinutesNumber(r.logged_minutes), 0) > toMinutesNumber(loggedHoursTask?.estimated_minutes)
                        ? '#dc3545'
                        : '#198754',
                  }}
                >
                  {formatHours(loggedHoursBreakdown.reduce((sum, r) => sum + toMinutesNumber(r.logged_minutes), 0))}
                </span>
              </div>
            </div>
          </>
        )}
      </Modal>
    </div>
  )
}
